/* ---------------------------------------------------------------------------
   How big a defect is, per category.

   `QC-02`. DoR's inspection forms have three boxes per line item — condition,
   extent, urgency — and a column headed *Remarks, Sketch Reference No.* Nothing
   on the paper is a quantity. But the manual asks for one twice:

     §4.3  "Where the inspector identifies the need for maintenance or repair
            work to be carried out it is useful to make an estimate of the
            approximate quantity of work involved — for instance the volume of
            gabions required for abutment protection work — so that budget cost
            estimates can be prepared."

     §5.4  "A note of the approximate quantities involved will assist later in
            the preparation of cost estimates."

   Both times it asks for the quantity so that a COST can be prepared, which is
   why this module and src/data/estimate.ts are two halves of one thing. What the
   form does with the answer is leave it in a remarks box in somebody's
   handwriting.

   THE VOCABULARY IS PER CATEGORY, not global. A crack has a length and a width;
   a scour hole has a depth below bed level; an eroded bank has a volume of
   gabions. One `quantity: number` field would have to be read against a unit
   stored somewhere else, and the first time two records of the same kind arrived
   in different units and were summed, the register would produce a confident
   wrong number — which is the failure mode this project spends most of its
   design on avoiding.

   So each of the nine categories declares its own measurements, out of a shared
   set of named dimensions on `DefectQuantity`. Two codes measuring an area both
   write `areaM2`, so "every spall over 2 m² on the network" is one query. Two
   codes measuring different things never share a field.

   NOTHING HERE IS REQUIRED. A defect somebody saw is worth recording whether or
   not they had a tape with them, and a form that refused an unmeasured finding
   would lose the finding to save the measurement. What IS refused is a number
   with no account of where it came from — see `validateQuantity`.
   --------------------------------------------------------------------------- */

import type { DefectQuantity, MeasurementMethod } from './types.ts';

/**
 * The dimensions a defect can be measured in.
 *
 * Keys on `DefectQuantity`, so the storage columns, the dialog and the cost
 * engine all name a measurement the same way.
 */
export type QuantityKey =
  | 'lengthM'
  | 'widthMm'
  | 'areaM2'
  | 'volumeM3'
  | 'depthMm'
  | 'depthM'
  | 'count'
  | 'sectionLossPct';

export interface QuantityField {
  key: QuantityKey;
  /** What this measurement is called FOR THIS CATEGORY — a joint's `widthMm` is its gap, not its width. */
  label: string;
  /** Printed beside the box and stored in the column name. Never implied. */
  unit: string;
  /**
   * The largest value this register will accept.
   *
   * A refusal ceiling, not a design limit. Every one of these is well past
   * anything on a Nepali road and comfortably short of a transcription error —
   * a crack 20 000 m long is a misplaced decimal, and storing it would put a
   * nonsense figure into a budget line that nobody would question because it
   * came out of a computer.
   */
  max: number;
  /** Integer-only where the thing being counted cannot be fractional. */
  integer?: boolean;
  /** What drives treatment selection and cost. Secondary measurements are context. */
  primary: boolean;
  hint?: string;
}

const LENGTH = (label: string, hint?: string): QuantityField => ({
  key: 'lengthM',
  label,
  unit: 'm',
  max: 2_000,
  primary: true,
  ...(hint ? { hint } : {}),
});

const AREA = (label: string, hint?: string): QuantityField => ({
  key: 'areaM2',
  label,
  unit: 'm²',
  max: 20_000,
  primary: true,
  ...(hint ? { hint } : {}),
});

/**
 * Which measurements each category takes, primary first.
 *
 * Keyed by the three-letter codes in ./defects.ts, which are the modeller's own
 * and are drift-checked against the vendored source. A code with no entry here
 * takes no measurements rather than taking all of them — see `fieldsFor`.
 */
export const QUANTITY_FIELDS: Readonly<Record<string, readonly QuantityField[]>> = {
  CRK: [
    LENGTH('Crack length', 'Along the crack, not the element'),
    {
      key: 'widthMm',
      label: 'Maximum crack width',
      unit: 'mm',
      max: 500,
      primary: true,
      /* The one measurement in this file with published thresholds behind it.
         Appendix D of the guideline rates concrete deck members by it, and
         ACI 546R / EN 1504-9 select the repair method by it — see
         ./ratingGuide.ts and ./remedial.ts. */
      hint: 'Widest point, read off a crack width gauge. 0.3 mm and 1 mm are both rating thresholds',
    },
  ],
  SPA: [
    AREA('Spalled area'),
    { key: 'depthMm', label: 'Spall depth', unit: 'mm', max: 5_000, primary: true },
  ],
  SCA: [
    AREA('Scaled area'),
    { key: 'depthMm', label: 'Scaling depth', unit: 'mm', max: 5_000, primary: false },
  ],
  REE: [
    AREA('Area of exposed reinforcement'),
    { key: 'count', label: 'Bars exposed', unit: 'bars', max: 10_000, integer: true, primary: true },
    {
      key: 'sectionLossPct',
      label: 'Estimated section loss',
      unit: '%',
      max: 100,
      primary: true,
      hint: 'What drives partial-depth repair versus replacement',
    },
  ],
  POH: [
    { key: 'count', label: 'Number of potholes', unit: 'potholes', max: 10_000, integer: true, primary: true },
    AREA('Total pothole area'),
    { key: 'depthMm', label: 'Greatest depth', unit: 'mm', max: 5_000, primary: false },
  ],
  SEP: [
    {
      key: 'widthMm',
      label: 'Joint gap',
      unit: 'mm',
      max: 500,
      primary: true,
      hint: 'Measured across the opening at the time of inspection',
    },
    LENGTH('Length of joint affected', 'Across the carriageway, per joint'),
  ],
  SCO: [
    {
      key: 'depthM',
      label: 'Scour depth below bed level',
      unit: 'm',
      max: 100,
      primary: true,
      hint: 'Metres, not millimetres — this is the one depth on this form measured with a rod',
    },
    LENGTH('Extent around the foundation', 'Along the perimeter of the affected pier or abutment'),
    { key: 'volumeM3', label: 'Bed protection required', unit: 'm³', max: 50_000, primary: true },
  ],
  SET: [
    {
      key: 'depthMm',
      label: 'Settlement',
      unit: 'mm',
      max: 5_000,
      primary: true,
      hint: 'Vertical movement against a level or a known datum',
    },
  ],
  ERO: [
    AREA('Eroded area'),
    {
      key: 'volumeM3',
      label: 'Gabion or rip-rap required',
      unit: 'm³',
      max: 50_000,
      primary: true,
      hint: 'The example §4.3 of the guideline gives, in the unit it gives it in',
    },
  ],
};

/** The measurements this category takes. Empty for a code with none. */
export function fieldsFor(code: string): readonly QuantityField[] {
  return QUANTITY_FIELDS[code] ?? [];
}

/** Every dimension any category uses. What the storage columns are derived from. */
export const QUANTITY_KEYS: readonly QuantityKey[] = [
  'lengthM',
  'widthMm',
  'areaM2',
  'volumeM3',
  'depthMm',
  'depthM',
  'count',
  'sectionLossPct',
];

/* --------------------------- how it was obtained --------------------------- */

export const MEASUREMENT_METHODS: readonly MeasurementMethod[] = [
  'measured',
  'photo-derived',
  'model-derived',
  'estimated',
  'not-measured',
];

const METHOD_LABEL: Record<MeasurementMethod, string> = {
  measured: 'Measured',
  'photo-derived': 'From a photograph',
  'model-derived': 'From the model',
  estimated: 'Estimated',
  'not-measured': 'Not measured',
};

/**
 * One sentence on what each method claims, for the dialog and for the estimate.
 *
 * Written as claims rather than as instructions, because that is what they
 * become: `QC-06` prints these under a cost, and a reader deciding whether to
 * trust a figure is entitled to know which of the four produced its quantities.
 */
const METHOD_NOTE: Record<MeasurementMethod, string> = {
  measured: 'Read off an instrument at the structure. The only method a DoR-format estimate can be built on without qualification.',
  'photo-derived':
    'Traced on a photograph against a reference of known size. Good for lengths and areas; the tool refuses a width the image cannot resolve.',
  'model-derived': 'Computed from geometry this register holds, not read at the structure.',
  estimated: 'The inspector’s judgement of the size, without an instrument. An estimate built on these says so wherever it is shown.',
  'not-measured': 'Looked at and not measured — which is a different statement from an issue recorded before this form had a quantity box.',
};

export function methodLabel(m: string): string {
  return METHOD_LABEL[m as MeasurementMethod] ?? m;
}

export function methodNote(m: string): string {
  return METHOD_NOTE[m as MeasurementMethod] ?? '';
}

export function isMeasurementMethod(v: string): v is MeasurementMethod {
  return (MEASUREMENT_METHODS as readonly string[]).includes(v);
}

/* --------------------------- refusals --------------------------- */

/**
 * What is wrong with a quantity, or null if nothing is.
 *
 * ONE FUNCTION, BOTH SIDES. The dialog calls it to refuse before sending and the
 * API calls it to refuse on arrival, so there is no way for the two to disagree
 * about what a valid measurement is — the same arrangement `isKnownElement`
 * already has, and for the same reason.
 *
 * Four rules, in the order they matter:
 *
 *   1. A quantity must say how it was obtained. This is the whole point of the
 *      item. A number with no method reaching the cost engine would be
 *      indistinguishable from a measured one by the time anybody read it.
 *   2. `not-measured` carries no numbers. The two together are a contradiction —
 *      "I did not measure it, and it is 2.4 m²" — and whichever half a reader
 *      believed would be the wrong one.
 *   3. A measurement must be one this category takes. A scour depth on a
 *      pothole is a transcription error, and accepting it would put a value in a
 *      column the cost engine reads for something else.
 *   4. Values are finite, not negative, within the ceiling, and whole where the
 *      thing counted cannot be fractional.
 */
export function validateQuantity(code: string, q: DefectQuantity): string | null {
  if (!isMeasurementMethod(q.method)) {
    return `"${String(q.method)}" is not one of: ${MEASUREMENT_METHODS.join(', ')}.`;
  }

  const allowed = new Map(fieldsFor(code).map((f) => [f.key, f]));
  const given = QUANTITY_KEYS.filter((k) => q[k] !== undefined && q[k] !== null);

  if (q.method === 'not-measured') {
    if (given.length) {
      return 'An issue recorded as not measured cannot also carry a measurement.';
    }
    return null;
  }

  for (const key of given) {
    const field = allowed.get(key);
    if (!field) {
      return `A "${code}" issue does not take a ${key} measurement.`;
    }
    const value = q[key] as number;
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return `"${field.label}" must be a number.`;
    }
    if (value < 0) return `"${field.label}" cannot be negative.`;
    if (value > field.max) {
      return `"${field.label}" is ${value} ${field.unit}, above the ${field.max} ${field.unit} this register accepts.`;
    }
    if (field.integer && !Number.isInteger(value)) {
      return `"${field.label}" must be a whole number.`;
    }
  }

  if (q.instrument !== undefined && q.instrument.length > 60) {
    return 'The instrument name is over 60 characters.';
  }

  /* A method of `measured` with nothing measured is the same contradiction as
     rule 2 the other way round, and it is the one an empty form produces by
     accident. Caught here rather than stored as a claim about an instrument
     reading that does not exist. */
  if (!given.length) {
    return 'A quantity has to include at least one measurement, or be recorded as not measured.';
  }

  return null;
}

/**
 * Whether a quantity says anything at all.
 *
 * An empty draft produces `{ method: 'not-measured' }`, which is a valid record
 * and a meaningless one to store on every defect ever logged. This is what the
 * write paths use to decide between storing it and storing null, so that a
 * defect nobody measured is byte-identical to one recorded before the field
 * existed.
 */
export function isEmptyQuantity(q: DefectQuantity | null): boolean {
  if (!q) return true;
  if (q.method !== 'not-measured') return false;
  if (q.instrument) return false;
  return !QUANTITY_KEYS.some((k) => q[k] !== undefined && q[k] !== null);
}

/* --------------------------- reading one back --------------------------- */

/**
 * A quantity as one line of text — "2.4 m² · 40 mm deep · measured (tape)".
 *
 * Used by the record, the report and the printed summary sheet, which is why it
 * is here rather than in any of them. The method is always the last part and is
 * never omitted: a size with no provenance beside it reads as a fact, and half
 * of these are somebody's estimate.
 */
export function describeQuantity(code: string, q: DefectQuantity): string {
  const parts = fieldsFor(code)
    .filter((f) => q[f.key] !== undefined && q[f.key] !== null)
    .map((f) => `${trimNumber(q[f.key] as number)} ${f.unit}`);

  /* The instrument is printed for both methods that HAVE one — a gauge reading
     and a photograph's scale reference are the same kind of claim, and the
     commonest way either is wrong is that the reference was mismeasured. It is
     omitted for an estimate, where recording an instrument would be a claim that
     one was used. */
  const how =
    (q.method === 'measured' || q.method === 'photo-derived') && q.instrument
      ? `${methodLabel(q.method).toLowerCase()} (${q.instrument})`
      : methodLabel(q.method).toLowerCase();

  return parts.length ? `${parts.join(' · ')} · ${how}` : how;
}

/** `2.40` -> `2.4`, `3.00` -> `3`. Trailing zeros on a measurement claim a precision nobody read. */
export function trimNumber(v: number): string {
  return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(3)));
}
